import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  Modal,
  Result,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd'
import { useNavigate } from 'react-router-dom'
import B2BLayout from '../../layouts/b2b'
import {
  activateDomain,
  checkDomain,
  createOnboardJob,
  deactivateDomain,
  deleteTenant,
  getDomains,
  getControlPlaneMeta,
  getPlatformModules,
  runProvisionJob,
} from './api'
import { sslPresentation } from './ssl-status'

type PlatformModule = {
  code: string
  name: string
  description?: string | null
  is_active?: boolean
  is_required?: boolean
  sort_order?: number
}

function defaultModuleCodes(rows: PlatformModule[]) {
  const codes = rows.filter(row => row.is_required).map(row => row.code)
  if (rows.some(row => row.code === 'documents') && !codes.includes('documents')) {
    codes.push('documents')
  }
  return codes
}

function normalizeBaseDomain(value: string): string {
  return String(value || '')
}

export default function AdminDomainsPage() {
  const navigate = useNavigate()
  const [meta, setMeta] = useState<{ dealer_base_domain?: string | null } | null>(null)
  const PLATFORM_BASE_DOMAIN = normalizeBaseDomain(
    String(meta?.dealer_base_domain || 'in.ze-okna.ru'),
  )
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<any[]>([])
  const [onboardForm] = Form.useForm()
  const [moduleCatalog, setModuleCatalog] = useState<PlatformModule[]>([])
  const [checkModal, setCheckModal] = useState<{
    open: boolean
    hostname: string | null
    dnsOk: boolean
    dnsAddresses: string[]
    httpsOk: boolean
    httpsError: string | null
    tls: any
  }>({
    open: false,
    hostname: null,
    dnsOk: false,
    dnsAddresses: [],
    httpsOk: false,
    httpsError: null,
    tls: null,
  })
  const [errorModal, setErrorModal] = useState<{ open: boolean; text: string }>({
    open: false,
    text: '',
  })
  const [deleteModal, setDeleteModal] = useState<{
    open: boolean
    hostname: string | null
  }>({
    open: false,
    hostname: null,
  })
  const [runningIds, setRunningIds] = useState<Record<string, boolean>>({})

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await getDomains()
      setData(result?.data || [])
    } catch (e) {
      setError(String(e))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      try {
        const result = await getControlPlaneMeta()
        if (cancelled) return
        setMeta(result?.data || null)
      } catch (_e) {
        // fallback to default base domain
        if (cancelled) return
        setMeta(null)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      try {
        const result = await getPlatformModules()
        if (cancelled) return
        const rows = (result?.data || []).filter(
          (row: PlatformModule) => row?.is_active !== false && Boolean(row?.code),
        )
        setModuleCatalog(rows)
        const selected = onboardForm.getFieldValue('modules')
        if (!Array.isArray(selected) || selected.length === 0) {
          onboardForm.setFieldsValue({ modules: defaultModuleCodes(rows) })
        }
      } catch (_e) {
        if (!cancelled) setModuleCatalog([])
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [onboardForm])

  const anyRunning = useMemo(() => {
    return data.some((row: any) => {
      const st = String(row?.provisioning_job?.status || '')
      return st === 'running' || st === 'pending'
    })
  }, [data])

  useEffect(() => {
    if (!anyRunning) return
    const id = window.setInterval(load, 3000)
    return () => window.clearInterval(id)
  }, [anyRunning, load])

  const onCreateOnboardJob = useCallback(async () => {
    try {
      const values = await onboardForm.validateFields()
      const requiredCodes = moduleCatalog.filter(row => row.is_required).map(row => row.code)
      const selectedModules = Array.from(
        new Set([...(values.modules || []), ...requiredCodes].map(String).filter(Boolean)),
      )
      const payload = {
        tenantName: String(values.tenantName || '').trim(),
        // subdomainLabel optional — server can derive it from tenantName
        subdomainLabel: values.subdomainLabel
          ? String(values.subdomainLabel || '').trim()
          : undefined,
        baseDomain: PLATFORM_BASE_DOMAIN,
        modules: selectedModules,
      }
      const result = await createOnboardJob(payload)
      const jobId = String(result?.data?.job?.id || '')
      if (jobId) {
        await runProvisionJob(jobId, {})
      }
      onboardForm.resetFields()
      onboardForm.setFieldsValue({ modules: defaultModuleCodes(moduleCatalog) })
      await load()
    } catch (error: any) {
      const errorMessage = error?.errors?.[0]?.detail || 'Ошибка создания тенанта'
      setErrorModal({
        open: true,
        text: errorMessage,
      })
    }
  }, [PLATFORM_BASE_DOMAIN, load, moduleCatalog, onboardForm])

  const onRun = useCallback(
    async (row: any, force?: boolean) => {
      const jobId = String(row?.provisioning_job?.id || '')
      if (!jobId) {
        setErrorModal({
          open: true,
          text: 'У домена нет provisioning job. Создайте подключение через форму "Новый дилер" на этой странице.',
        })
        return
      }
      setRunningIds(prev => ({ ...prev, [jobId]: true }))
      try {
        const result = await runProvisionJob(jobId, force ? { force: true } : {})
        if (result?.errors?.length) {
          const msg = result.errors
            .map((e: any) => String(e?.detail || e?.message || '').trim())
            .filter(Boolean)
            .join('. ')
          setErrorModal({ open: true, text: msg || 'Не удалось запустить provisioning job' })
        }
      } finally {
        setRunningIds(prev => ({ ...prev, [jobId]: false }))
        await load()
      }
    },
    [load],
  )

  const onCheck = useCallback(async (row: any) => {
    const hostname = String(row?.hostname || '').trim()
    if (!hostname) return
    const result = await checkDomain(hostname)
    const data = result?.data || {}
    setCheckModal({
      open: true,
      hostname,
      dnsOk: data.dns?.ok || false,
      dnsAddresses: data.dns?.addresses || [],
      httpsOk: data.https?.ok || false,
      httpsError: data.https?.error || null,
      tls: data.https?.tls || null,
    })
  }, [])

  const onToggleActive = useCallback(
    async (row: any) => {
      const hostname = String(row?.hostname || '').trim()
      if (!hostname) return
      const isActive = String(row?.status || '') === 'active'
      try {
        if (isActive) {
          await deactivateDomain(hostname)
        } else {
          await activateDomain(hostname)
        }
        await load()
      } catch (e) {
        setErrorModal({ open: true, text: String(e) })
      }
    },
    [load],
  )

  const onDelete = useCallback(async (row: any) => {
    const hostname = String(row?.hostname || '').trim()
    if (!hostname) return
    setDeleteModal({ open: true, hostname })
  }, [])

  const confirmDelete = useCallback(async () => {
    const hostname = deleteModal.hostname
    if (!hostname) return
    try {
      await deleteTenant(hostname)
      setDeleteModal({ open: false, hostname: null })
      await load()
    } catch (e) {
      setErrorModal({ open: true, text: String(e) })
    }
  }, [deleteModal.hostname, load])

  const breadcrumbs = [{ href: '/admin/dealers/domains', title: 'Поддомены дилеров' }]
  const ssl = sslPresentation(checkModal.tls)

  const stepTitle = (step?: string | null) => {
    const s = String(step || '')
    const map: Record<string, string> = {
      init: 'Инициализация',
      domain_reserve: 'Регистрация домена',
      db_provision: 'Создание БД/пользователя',
      migrate: 'Миграции (и сидирование)',
      activate: 'Активация домена/подписки',
    }
    return map[s] || (s ? `Шаг: ${s}` : '—')
  }

  const pipelineLong = [
    'Регистрация/резерв домена (master domain_registry)',
    'Создание data source и job (master)',
    'Создание роли/БД в Postgres',
    'Применение миграций тенанта',
    'Сидирование (может быть отключено флагами)',
    'Активация домена и подписки',
    'Проверка доступности домена (DNS/HTTP)',
    'Получение SSL (Traefik/Let’s Encrypt) и проверка срока действия',
  ]

  return (
    <B2BLayout breadcrumbs={breadcrumbs} pageTitle={'Поддомены дилеров'}>
      {error && <Result status="error" title="Ошибка загрузки доменов" subTitle={error} />}
      {!error && (
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <Alert
            type="info"
            showIcon
            message="Подключения дилеров"
            description={
              <div>
                <div>{`Резервирование и управление доменами третьего уровня вида dealer_name.${PLATFORM_BASE_DOMAIN}.`}</div>
                <Typography.Text type="secondary">
                  Пайплайн запуска дилера включает: {pipelineLong.join(' → ')}
                </Typography.Text>
              </div>
            }
          />

          <Card title="Новый дилер (создать подключение и запустить пайплайн)">
            <Form
              form={onboardForm}
              layout="vertical"
              initialValues={{
                modules: [],
              }}
            >
              <Space wrap style={{ width: '100%' }} size={16} align="start">
                <Form.Item
                  name="tenantName"
                  label="Название дилера"
                  rules={[{ required: true, message: 'Укажите название дилера' }]}
                  style={{ minWidth: 320 }}
                >
                  <Input placeholder='ООО "Ромашка"' />
                </Form.Item>
                <Form.Item name="subdomainLabel" label="Поддомен" style={{ minWidth: 240 }}>
                  <Input placeholder="dealer-name (необязательно)" />
                </Form.Item>
                <Form.Item label="Базовый домен" style={{ minWidth: 260 }}>
                  <Input value={PLATFORM_BASE_DOMAIN} disabled />
                </Form.Item>
              </Space>

              <Space wrap style={{ width: '100%' }} size={16} align="start">
                <Form.Item
                  name="modules"
                  label="Модули"
                  extra="Обязательные модули нельзя отключить"
                >
                  <Checkbox.Group>
                    <Space direction="vertical" size={8}>
                      {moduleCatalog.map(row => (
                        <Checkbox
                          key={row.code}
                          value={row.code}
                          disabled={Boolean(row.is_required)}
                        >
                          <span>{row.name}</span>
                          {row.description ? (
                            <Typography.Text
                              type="secondary"
                              style={{ display: 'block', fontSize: 12, fontWeight: 400 }}
                            >
                              {row.description}
                            </Typography.Text>
                          ) : null}
                        </Checkbox>
                      ))}
                    </Space>
                  </Checkbox.Group>
                </Form.Item>
              </Space>

              <Button type="primary" onClick={onCreateOnboardJob}>
                Создать и запустить
              </Button>
            </Form>
          </Card>
          <Table
            rowKey="id"
            loading={loading}
            dataSource={data}
            pagination={{ pageSize: 20 }}
            onRow={(row: any) => ({
              onClick: () => {
                const tenantId = String(row?.tenant_id || '')
                if (tenantId) navigate(`/admin/dealers/domains/${tenantId}`)
              },
              style: { cursor: 'pointer' },
            })}
            columns={[
              { title: 'Имя хоста', dataIndex: 'hostname', key: 'hostname' },
              {
                title: 'Тенант',
                key: 'tenant',
                render: (_, row: any) => (
                  <div style={{ lineHeight: 1.2 }}>
                    <div>{row?.tenant?.name || '—'}</div>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {row?.tenant?.dealer_code}
                    </Typography.Text>
                  </div>
                ),
              },
              {
                title: 'Кем создан',
                key: 'created_by',
                render: (_, row: any) => {
                  if (row?.created_by == null) {
                    return <Typography.Text type="secondary">Самостоятельно</Typography.Text>
                  }
                  return row?.manager_name ? (
                    <span>{row.manager_name}</span>
                  ) : (
                    <Typography.Text type="secondary">ID: {row.created_by}</Typography.Text>
                  )
                },
              },
              {
                title: 'Активность',
                key: 'domain_status',
                render: (_, row: any) => {
                  const s = String(row?.status || '')
                  const domainStatusMap: Record<string, { label: string; color: string }> = {
                    active: { label: 'Активен', color: 'green' },
                    reserved: { label: 'Зарезервирован', color: 'orange' },
                    inactive: { label: 'Неактивен', color: 'default' },
                    deactivated: { label: 'Деактивирован', color: 'default' },
                    pending: { label: 'Ожидание', color: 'blue' },
                  }
                  const mapped = domainStatusMap[s]
                  return <Tag color={mapped?.color}>{mapped?.label || s || '—'}</Tag>
                },
              },
            ]}
          />
          <Modal
            open={checkModal.open}
            title={`Проверка домена: ${checkModal.hostname}`}
            onCancel={() =>
              setCheckModal({
                open: false,
                hostname: null,
                dnsOk: false,
                dnsAddresses: [],
                httpsOk: false,
                httpsError: null,
                tls: null,
              })
            }
            footer={[
              <Button
                key="close"
                onClick={() =>
                  setCheckModal({
                    open: false,
                    hostname: null,
                    dnsOk: false,
                    dnsAddresses: [],
                    httpsOk: false,
                    httpsError: null,
                    tls: null,
                  })
                }
              >
                Закрыть
              </Button>,
            ]}
          >
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <div>
                <Typography.Text strong>DNS:</Typography.Text>
                {checkModal.dnsOk ? (
                  <Tag color="green" style={{ marginLeft: 8 }}>
                    OK
                  </Tag>
                ) : (
                  <Tag color="red" style={{ marginLeft: 8 }}>
                    Ошибка
                  </Tag>
                )}
                {checkModal.dnsAddresses.length > 0 && (
                  <div style={{ marginTop: 4 }}>
                    <Typography.Text type="secondary">
                      IP адреса: {checkModal.dnsAddresses.join(', ')}
                    </Typography.Text>
                  </div>
                )}
              </div>
              <div>
                <Typography.Text strong>HTTPS:</Typography.Text>
                {checkModal.httpsOk ? (
                  <Tag color="green" style={{ marginLeft: 8 }}>
                    OK
                  </Tag>
                ) : (
                  <Tag color="red" style={{ marginLeft: 8 }}>
                    Ошибка
                  </Tag>
                )}
                {checkModal.httpsError && (
                  <div style={{ marginTop: 4 }}>
                    <Typography.Text type="danger">{checkModal.httpsError}</Typography.Text>
                  </div>
                )}
              </div>
              {ssl && (
                <div>
                  <Typography.Text strong>SSL сертификат:</Typography.Text>
                  <Tag color={ssl.color} style={{ marginLeft: 8 }}>
                    {ssl.label}
                  </Tag>
                  <div style={{ marginTop: 4 }}>
                    <Typography.Text type="secondary">
                      Subject: {checkModal.tls.subject?.CN || '—'}
                    </Typography.Text>
                  </div>
                  <div>
                    <Typography.Text type="secondary">
                      Issuer: {checkModal.tls.issuer?.O || checkModal.tls.issuer?.CN || '—'}
                    </Typography.Text>
                  </div>
                  <div>
                    <Typography.Text type="secondary">
                      Действует с: {checkModal.tls.valid_from || '—'}
                    </Typography.Text>
                  </div>
                  <div>
                    <Typography.Text type="secondary">
                      Действует до: {checkModal.tls.valid_to || '—'}
                    </Typography.Text>
                  </div>
                  {ssl.hint && (
                    <div style={{ marginTop: 4 }}>
                      <Typography.Text type="danger">{ssl.hint}</Typography.Text>
                    </div>
                  )}
                </div>
              )}
              {!checkModal.tls && checkModal.httpsOk && (
                <div>
                  <Typography.Text strong>SSL сертификат:</Typography.Text>
                  <Tag color="orange" style={{ marginLeft: 8 }}>
                    Не выпущен
                  </Tag>
                </div>
              )}
            </Space>
          </Modal>

          <Modal
            open={errorModal.open}
            title="Ошибка"
            onCancel={() => setErrorModal({ open: false, text: '' })}
            footer={[
              <Button key="close" onClick={() => setErrorModal({ open: false, text: '' })}>
                Закрыть
              </Button>,
            ]}
          >
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
              {errorModal.text}
            </pre>
          </Modal>

          <Modal
            open={deleteModal.open}
            title="Подтверждение удаления"
            onCancel={() => setDeleteModal({ open: false, hostname: null })}
            footer={[
              <Button key="cancel" onClick={() => setDeleteModal({ open: false, hostname: null })}>
                Отмена
              </Button>,
              <Button key="confirm" danger type="primary" onClick={confirmDelete}>
                Удалить
              </Button>,
            ]}
          >
            <p>
              Вы уверены, что хотите удалить тенант {deleteModal.hostname}?<br />
              Это действие удалит:
              <br />
              - БД PostgreSQL
              <br />
              - Пользователя БД
              <br />
              - Все данные тенанта
              <br />- Домен из реестра
            </p>
          </Modal>
        </Space>
      )}
    </B2BLayout>
  )
}
